import * as React from "react";
import { LuArrowRight } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { DESTINATIONS_SHOWCASE_STYLES } from "./constants";
import type { DestinationsShowcaseProps, IDestinationThumbnail } from "./types";

const NO_LINK_PLACEHOLDER = "#";

function resolveThumbHref(thumb: IDestinationThumbnail): string | undefined {
  const href = thumb.href?.trim();
  return href && href !== NO_LINK_PLACEHOLDER ? href : undefined;
}

function sendThumbAnalytics(thumb: IDestinationThumbnail) {
  if (!thumb.analyticsData || !thumb.analyticsUrl) {
    return;
  }

  try {
    const payload = btoa(unescape(encodeURIComponent(JSON.stringify(thumb.analyticsData))));
    navigator.sendBeacon(thumb.analyticsUrl, payload);
  } catch {
    // Ignore analytics failures to avoid impacting UX
  }
}

export const DestinationsShowcase = React.forwardRef<
  HTMLElement,
  DestinationsShowcaseProps
>(
  (
    {
      className,
      title,
      description,
      // No longer a banner: the mosaic leads with the first destination's own
      // photo, and the editor's hero image only backs up a card without one.
      heroImageUrl,
      thumbnails,
      openLinksInNewTab = false,
      exploreHref,
      exploreLabel,
      onThumbnailClick,
      onExploreClick,
      ...props
    },
    ref
  ) => {
    const linkProps = openLinksInNewTab
      ? { target: "_blank" as const, rel: "noopener noreferrer" }
      : {};
    const showExploreLink = Boolean(exploreHref && exploreLabel);

    return (
      <section
        ref={ref}
        className={cn(DESTINATIONS_SHOWCASE_STYLES.root, className)}
        {...props}
      >
        <div className={DESTINATIONS_SHOWCASE_STYLES.header}>
          <div className={DESTINATIONS_SHOWCASE_STYLES.headerCopy}>
            <h2 className={DESTINATIONS_SHOWCASE_STYLES.title}>{title}</h2>
            <p className={DESTINATIONS_SHOWCASE_STYLES.description}>
              {description}
            </p>
          </div>
          {showExploreLink && (
            <a
              href={exploreHref}
              className={cn("group/explore", DESTINATIONS_SHOWCASE_STYLES.exploreLink)}
              data-testid="destinations-explore-link"
              onClick={onExploreClick}
            >
              {exploreLabel}
              <LuArrowRight className={DESTINATIONS_SHOWCASE_STYLES.exploreIcon} aria-hidden />
            </a>
          )}
        </div>

        <div className={DESTINATIONS_SHOWCASE_STYLES.grid}>
          {thumbnails.map((thumb, index) => {
            const href = resolveThumbHref(thumb);
            const isFeatured = index === 0;
            const cardClassName = cn(
              DESTINATIONS_SHOWCASE_STYLES.card,
              isFeatured && DESTINATIONS_SHOWCASE_STYLES.cardFeatured,
              href && DESTINATIONS_SHOWCASE_STYLES.cardLinked
            );
            const content = (
              <>
                <img
                  src={thumb.imageUrl || heroImageUrl}
                  alt={thumb.name}
                  className={DESTINATIONS_SHOWCASE_STYLES.cardImage}
                  loading="lazy"
                  decoding="async"
                />
                <div className={DESTINATIONS_SHOWCASE_STYLES.cardOverlay} />
                <div className={DESTINATIONS_SHOWCASE_STYLES.cardContent}>
                  <span className={DESTINATIONS_SHOWCASE_STYLES.cardLocation}>
                    {thumb.location}
                  </span>
                  <span
                    className={cn(
                      DESTINATIONS_SHOWCASE_STYLES.cardName,
                      isFeatured && DESTINATIONS_SHOWCASE_STYLES.cardNameFeatured
                    )}
                  >
                    {thumb.name}
                  </span>
                </div>
              </>
            );

            if (!href) {
              return (
                <div
                  key={thumb.id}
                  className={cardClassName}
                  data-testid={`destination-thumb-${thumb.id}`}
                  data-featured={isFeatured || undefined}
                >
                  {content}
                </div>
              );
            }

            return (
              <a
                key={thumb.id}
                href={href}
                className={cardClassName}
                data-testid={`destination-thumb-${thumb.id}`}
                data-featured={isFeatured || undefined}
                onClick={(event) => {
                  sendThumbAnalytics(thumb);
                  onThumbnailClick?.(thumb, event);
                }}
                {...linkProps}
              >
                {content}
              </a>
            );
          })}
        </div>
      </section>
    );
  }
);

DestinationsShowcase.displayName = "DestinationsShowcase";
